import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, Grid, Alert, IconButton, Tabs, Tab, Switch, FormControlLabel,
  InputAdornment, Collapse, Divider, CircularProgress, Tooltip,
} from '@mui/material';
import {
  Search, Sync, Add, Edit, Delete, ExpandMore, ExpandLess, CheckCircle, Cancel,
} from '@mui/icons-material';
import axios from 'axios';

const BROKERS = ['shoonya', 'flattrade', 'aliceblue'];
const SOURCES = ['tradingview', 'chartink', 'custom'];

export default function SymbolMaster({ apiUrl }) {
  const [symbols, setSymbols] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [fnoOnly, setFnoOnly] = useState(true);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editSym, setEditSym] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchSymbols = useCallback(async () => {
    setLoading(true);
    try {
      const params = { fno_only: fnoOnly, limit: 300 };
      if (search) params.search = search;
      const res = await axios.get(`${apiUrl}/symbols/`, { headers, params });
      setSymbols(res.data.symbols);
      setTotal(res.data.total);
    } catch (err) {
      setError('Failed to fetch symbols');
    } finally {
      setLoading(false);
    }
  }, [apiUrl, search, fnoOnly]);

  useEffect(() => { fetchSymbols(); }, [fetchSymbols]);

  const handleSyncNSE = async () => {
    setSyncing(true); setError(''); setSuccess('');
    try {
      const res = await axios.post(`${apiUrl}/symbols/sync-nse`, {}, { headers });
      setSuccess(res.data.message);
      fetchSymbols();
    } catch (err) {
      setError(err.response?.data?.detail || 'Sync failed');
    } finally { setSyncing(false); }
  };

  const handleSyncBroker = async () => {
    setSyncing(true); setError(''); setSuccess('');
    try {
      const res = await axios.post(`${apiUrl}/symbols/sync-broker-tokens?broker=shoonya`, {}, { headers });
      setSuccess(res.data.message);
      fetchSymbols();
    } catch (err) {
      setError(err.response?.data?.detail || 'Broker sync failed');
    } finally { setSyncing(false); }
  };

  const handleDelete = async (id, nse) => {
    if (!window.confirm(`Delete ${nse} from master list?`)) return;
    try {
      await axios.delete(`${apiUrl}/symbols/${id}`, { headers });
      setSuccess(`${nse} deleted`);
      fetchSymbols();
    } catch (err) {
      setError('Delete failed');
    }
  };

  const openEdit = (sym) => {
    setEditSym(JSON.parse(JSON.stringify(sym)));
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editSym) return;
    setLoading(true);
    try {
      await axios.put(`${apiUrl}/symbols/${editSym.id}`, {
        company_name: editSym.company_name,
        lot_size: editSym.lot_size,
        is_fno: editSym.is_fno,
        is_active: editSym.is_active,
        broker_mappings: editSym.broker_mappings,
        source_mappings: editSym.source_mappings,
      }, { headers });
      setSuccess(`${editSym.nse_symbol} updated`);
      setEditOpen(false);
      fetchSymbols();
    } catch (err) {
      setError(err.response?.data?.detail || 'Update failed');
    } finally { setLoading(false); }
  };

  // ── Add Symbol ─────────────────────────────
  const [addForm, setAddForm] = useState({ nse_symbol: '', company_name: '', lot_size: 1, is_fno: true });
  const handleAdd = async () => {
    try {
      const res = await axios.post(`${apiUrl}/symbols/`, addForm, { headers });
      setSuccess(res.data.message);
      setAddOpen(false);
      setAddForm({ nse_symbol: '', company_name: '', lot_size: 1, is_fno: true });
      fetchSymbols();
    } catch (err) {
      setError(err.response?.data?.detail || 'Add failed');
    }
  };

  const mapped = (sym, broker) => {
    const bm = sym.broker_mappings?.[broker];
    return bm && (bm.eq_token || bm.fut_token);
  };

  return (
    <Box data-testid="symbol-master-page">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={700}>Symbol Master</Typography>
        <Box display="flex" gap={1}>
          <Button variant="outlined" size="small" startIcon={syncing ? <CircularProgress size={14} /> : <Sync />}
            onClick={handleSyncNSE} disabled={syncing} data-testid="sync-nse-btn">
            Sync NSE F&O
          </Button>
          <Button variant="outlined" size="small" color="secondary"
            startIcon={syncing ? <CircularProgress size={14} /> : <Sync />}
            onClick={handleSyncBroker} disabled={syncing} data-testid="sync-broker-btn">
            Sync Broker Tokens
          </Button>
          <Button variant="contained" size="small" startIcon={<Add />}
            onClick={() => setAddOpen(true)} data-testid="add-symbol-btn">
            Add Symbol
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 1 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Search & Filters */}
      <Paper sx={{ p: 1.5, mb: 2 }} variant="outlined">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" placeholder="Search symbol or company..."
              value={search} onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
              data-testid="symbol-search"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControlLabel control={<Switch checked={fnoOnly} onChange={(e) => setFnoOnly(e.target.checked)} size="small" />}
              label="F&O Only" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="body2" color="text.secondary">
              Showing {symbols.length} of {total} symbols
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Symbol Table */}
      <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, width: 30 }}></TableCell>
              <TableCell sx={{ fontWeight: 700 }}>NSE Symbol</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Company</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Lot Size</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>F&O</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Shoonya</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Flattrade</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>AliceBlue</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>TradingView</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Chartink</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {symbols.map((sym) => (
              <React.Fragment key={sym.id}>
                <TableRow hover sx={{ cursor: 'pointer' }}
                  onClick={() => setExpandedId(expandedId === sym.id ? null : sym.id)}
                  data-testid={`symbol-row-${sym.nse_symbol}`}
                >
                  <TableCell>
                    <IconButton size="small">
                      {expandedId === sym.id ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    </IconButton>
                  </TableCell>
                  <TableCell><Typography fontWeight="bold">{sym.nse_symbol}</Typography></TableCell>
                  <TableCell>{sym.company_name || '-'}</TableCell>
                  <TableCell align="right">{sym.lot_size}</TableCell>
                  <TableCell align="center">
                    <Chip label={sym.is_fno ? 'Yes' : 'No'} size="small"
                      color={sym.is_fno ? 'success' : 'default'} sx={{ fontSize: '0.7rem', height: 20 }} />
                  </TableCell>
                  {BROKERS.map((b) => (
                    <TableCell key={b} align="center">
                      <Tooltip title={mapped(sym, b) ? `Token: ${sym.broker_mappings?.[b]?.eq_token || '?'}` : 'Not mapped'}>
                        {mapped(sym, b)
                          ? <CheckCircle fontSize="small" color="success" />
                          : <Cancel fontSize="small" color="disabled" />}
                      </Tooltip>
                    </TableCell>
                  ))}
                  <TableCell align="center">
                    <Typography variant="caption">{sym.source_mappings?.tradingview || '-'}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="caption">{sym.source_mappings?.chartink || '-'}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); openEdit(sym); }}
                      data-testid={`edit-${sym.nse_symbol}`}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDelete(sym.id, sym.nse_symbol); }}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
                {/* Expanded detail row */}
                <TableRow>
                  <TableCell colSpan={11} sx={{ py: 0, borderBottom: expandedId === sym.id ? undefined : 'none' }}>
                    <Collapse in={expandedId === sym.id} unmountOnExit>
                      <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1, my: 1 }}>
                        <Grid container spacing={2}>
                          {BROKERS.map((b) => {
                            const bm = sym.broker_mappings?.[b] || {};
                            return (
                              <Grid item xs={12} md={4} key={b}>
                                <Typography variant="subtitle2" color="primary" gutterBottom>
                                  {b.charAt(0).toUpperCase() + b.slice(1)} Mapping
                                </Typography>
                                <Typography variant="body2">EQ Symbol: <strong>{bm.eq_symbol || '-'}</strong></Typography>
                                <Typography variant="body2">EQ Token: <strong>{bm.eq_token || '-'}</strong></Typography>
                                <Typography variant="body2">FUT Symbol: <strong>{bm.fut_symbol || '-'}</strong></Typography>
                                <Typography variant="body2">FUT Token: <strong>{bm.fut_token || '-'}</strong></Typography>
                              </Grid>
                            );
                          })}
                        </Grid>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="subtitle2" color="primary" gutterBottom>Signal Source Mappings</Typography>
                        <Grid container spacing={2}>
                          {SOURCES.map((s) => (
                            <Grid item xs={4} key={s}>
                              <Typography variant="body2">{s}: <strong>{sym.source_mappings?.[s] || '-'}</strong></Typography>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Dialog */}
      {editSym && (
        <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Edit {editSym.nse_symbol} Mappings</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Company Name" value={editSym.company_name || ''}
                  onChange={(e) => setEditSym({ ...editSym, company_name: e.target.value })} />
              </Grid>
              <Grid item xs={3}>
                <TextField fullWidth size="small" label="Lot Size" type="number" value={editSym.lot_size}
                  onChange={(e) => setEditSym({ ...editSym, lot_size: parseInt(e.target.value) || 1 })} />
              </Grid>
              <Grid item xs={3}>
                <FormControlLabel control={<Switch checked={editSym.is_fno}
                  onChange={(e) => setEditSym({ ...editSym, is_fno: e.target.checked })} />} label="F&O" />
              </Grid>

              {/* Broker Mappings */}
              {BROKERS.map((b) => {
                const bm = editSym.broker_mappings?.[b] || {};
                const update = (field, val) => {
                  const copy = { ...editSym };
                  if (!copy.broker_mappings) copy.broker_mappings = {};
                  if (!copy.broker_mappings[b]) copy.broker_mappings[b] = {};
                  copy.broker_mappings[b][field] = val;
                  setEditSym(copy);
                };
                return (
                  <React.Fragment key={b}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="primary">
                        {b.charAt(0).toUpperCase() + b.slice(1)} Broker
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <TextField fullWidth size="small" label="EQ Symbol" value={bm.eq_symbol || ''}
                        onChange={(e) => update('eq_symbol', e.target.value)} />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField fullWidth size="small" label="EQ Token" value={bm.eq_token || ''}
                        onChange={(e) => update('eq_token', e.target.value)} />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField fullWidth size="small" label="FUT Symbol" value={bm.fut_symbol || ''}
                        onChange={(e) => update('fut_symbol', e.target.value)} />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField fullWidth size="small" label="FUT Token" value={bm.fut_token || ''}
                        onChange={(e) => update('fut_token', e.target.value)} />
                    </Grid>
                  </React.Fragment>
                );
              })}

              {/* Source Mappings */}
              <Grid item xs={12}><Typography variant="subtitle2" color="primary">Signal Sources</Typography></Grid>
              {SOURCES.map((s) => (
                <Grid item xs={4} key={s}>
                  <TextField fullWidth size="small" label={s.charAt(0).toUpperCase() + s.slice(1)}
                    value={editSym.source_mappings?.[s] || ''}
                    onChange={(e) => {
                      const copy = { ...editSym };
                      if (!copy.source_mappings) copy.source_mappings = {};
                      copy.source_mappings[s] = e.target.value;
                      setEditSym(copy);
                    }} />
                </Grid>
              ))}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveEdit} disabled={loading}
              data-testid="save-symbol-btn">
              {loading ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Add Dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Symbol</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="NSE Symbol" value={addForm.nse_symbol}
                onChange={(e) => setAddForm({ ...addForm, nse_symbol: e.target.value.toUpperCase() })}
                required data-testid="add-nse-symbol" />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Company Name" value={addForm.company_name}
                onChange={(e) => setAddForm({ ...addForm, company_name: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Lot Size" type="number" value={addForm.lot_size}
                onChange={(e) => setAddForm({ ...addForm, lot_size: parseInt(e.target.value) || 1 })} />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel control={<Switch checked={addForm.is_fno}
                onChange={(e) => setAddForm({ ...addForm, is_fno: e.target.checked })} />} label="F&O Stock" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd} disabled={!addForm.nse_symbol}
            data-testid="save-add-symbol-btn">Add</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
